// === WORKFLOW SUMMARY — documents card ===
// Moved verbatim from WorkflowSummaryScreen: the "Documenten" card with the always-visible
// Algemene Voorwaarden link, the collapsible per-product document rows (voorwaarden +
// verzekeringskaart, driven by the content model in src/products/content.ts) and the
// SHOW_MISSING_CONTENT_MARKERS ONTBREEKT rows. Owns its two UI states
// (isProductDocumentsExpanded / isExpandToggleHovered) and reads data / selectedProducts
// from useWorkflow itself (pattern: ApplicantSummary, PremiumBreakdownCard).

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflow } from '@sluitstraat/core';
import { workflowSummaryStyles as styles } from '../../../styles/commonStyles';
import { getProductOrder } from '../../../workflowConfig';
import {
  GENERAL_TERMS,
  getProductContent,
  getProductDocuments,
} from '../../../products/content';
import { SHOW_MISSING_CONTENT_MARKERS } from '../../../config/uiFlags';

const DocumentFileIcon: React.FC = () => (
  <svg style={styles.documentIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="12" y1="18" x2="12" y2="12"></line>
    <line x1="9" y1="15" x2="15" y2="15"></line>
  </svg>
);

export const DocumentsCard: React.FC = () => {
  const { t } = useTranslation();
  const { data, selectedProducts } = useWorkflow();
  const [isProductDocumentsExpanded, setIsProductDocumentsExpanded] = useState(false);
  const [isExpandToggleHovered, setIsExpandToggleHovered] = useState(false);

  return (
    <div className="summary-docs-card" style={styles.documentsCard}>
      <h3 style={styles.documentsTitle}>{t('screens.offerSummary.documents.title')}</h3>
      <p style={styles.documentsSubtitle}>{t('screens.offerSummary.documents.subtitle')}</p>
      <div style={styles.documentsList}>
        {/* General Terms (Algemene Voorwaarden) - always visible above collapsible */}
        <a
          href={GENERAL_TERMS.url}
          download={GENERAL_TERMS.fileName}
          style={styles.documentLink}
        >
          <DocumentFileIcon />
          <div style={styles.documentLinkContent}>
            <span style={styles.documentLinkText}>{t('screens.offerSummary.documents.termsAndConditions')}</span>
            <span style={styles.documentLinkSubtext}>PDF</span>
          </div>
        </a>

        {/* Product documents — voorwaarden + verzekeringskaart per selected product,
            driven by the content model (src/products/content.ts). Documents that are
            still a TODO(content) marker render a visible ONTBREEKT row when
            SHOW_MISSING_CONTENT_MARKERS is on (dev) instead of silently dropping. */}
        {(() => {
          const hoofddekking = data['personenauto']?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
          const docRows = getProductOrder()
            .filter((id) => (selectedProducts as Record<string, boolean | undefined> | undefined)?.[id])
            .flatMap((id) =>
              getProductDocuments(id, id === 'personenauto' ? { coverage: hoofddekking } : undefined)
                .map((resolved) => ({ id, ...resolved })));
          const availableRows = docRows.filter((row) => row.doc !== null);
          const missingRows = SHOW_MISSING_CONTENT_MARKERS ? docRows.filter((row) => row.doc === null) : [];
          if (availableRows.length === 0 && missingRows.length === 0) return null;
          const kindLabel = (kind: 'voorwaarden' | 'verzekeringskaart' | 'algemeneVoorwaarden') => {
            if (kind === 'voorwaarden') return t('screens.offerSummary.documents.productTerms');
            if (kind === 'algemeneVoorwaarden') return t('screens.offerSummary.documents.generalAgreements');
            return t('screens.offerSummary.documents.insuranceCard');
          };
          return (
        <div style={styles.documentsContainer}>
          {/* Collapsible Product-Specific Documents Header */}
          <div
            onClick={() => setIsProductDocumentsExpanded(!isProductDocumentsExpanded)}
            onMouseEnter={() => setIsExpandToggleHovered(true)}
            onMouseLeave={() => setIsExpandToggleHovered(false)}
            style={{
              ...styles.expandToggleConnected,
              ...(isExpandToggleHovered && !isProductDocumentsExpanded ? styles.expandToggleHover : {}),
              ...(isProductDocumentsExpanded ? styles.expandToggleExpanded : {}),
            }}
          >
            <div style={styles.expandToggleLeft}>
              <div style={{
                ...styles.expandToggleIconWrapper,
                ...(isExpandToggleHovered ? styles.expandToggleIconWrapperHover : {}),
              }}>
                <svg
                  style={{
                    ...styles.expandToggleIcon,
                    ...(isProductDocumentsExpanded ? styles.expandToggleIconExpanded : {}),
                    ...(isExpandToggleHovered ? styles.expandToggleIconHover : {}),
                  }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <span style={{
                ...styles.expandToggleText,
                ...(isExpandToggleHovered ? styles.expandToggleTextHover : {}),
              }}>
                {isProductDocumentsExpanded ? t('screens.offerSummary.documents.hideProductDocuments') : t('screens.offerSummary.documents.showProductDocuments')}
              </span>
            </div>
            <span style={{
              ...styles.expandToggleBadge,
              ...(isExpandToggleHovered ? styles.expandToggleBadgeHover : {}),
            }}>
              {availableRows.length}
            </span>
          </div>

          {/* Collapsible Product-Specific Documents */}
          <div style={{
            ...styles.collapsibleDocsAnimator,
            ...(isProductDocumentsExpanded ? styles.collapsibleDocsOpen : styles.collapsibleDocsClosed),
          }}>
            <div style={styles.collapsibleDocumentsContainer}>
              {availableRows.map((row) => (
                <a
                  key={`${row.id}-${row.kind}`}
                  href={row.doc!.url}
                  download={row.doc!.fileName}
                  style={styles.documentLink}
                >
                  <DocumentFileIcon />
                  <div style={styles.documentLinkContent}>
                    <span style={styles.documentLinkText}>{kindLabel(row.kind)} - {t(getProductContent(row.id).offerNameKey)}</span>
                    <span style={styles.documentLinkSubtext}>PDF</span>
                  </div>
                </a>
              ))}
              {missingRows.map((row) => (
                <div
                  key={`${row.id}-${row.kind}-missing`}
                  style={{ ...styles.documentLink, ...styles.documentLinkMissing }}
                >
                  <DocumentFileIcon />
                  <div style={styles.documentLinkContent}>
                    <span style={styles.documentLinkText}>ONTBREEKT: {kindLabel(row.kind)} - {t(getProductContent(row.id).offerNameKey)}</span>
                    <span style={styles.documentLinkSubtext}>{row.todo}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* End of Collapsible Product-Specific Documents */}
        </div>
          );
        })()}
      </div>
    </div>
  );
};
